"use client";

import { articleTypeWithoutDhub } from "@utils/default";
import { getLink } from "@utils/linkUtils";
import { useLocale, useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useState } from "react";
// import "swiper/css";
// import "swiper/css/effect-fade";
// import "swiper/css/navigation";
// import "swiper/css/pagination";
import { Swiper, SwiperSlide } from "swiper/react";

import { CardCarouselBottomNavigation } from "@/components/AutogeneratedCarousel/CardCarouselBottomNavigation";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import Image from "@/components/ui/Image/Image";
import { ctaClickTracking } from "@/dataLayer/dataLayer";
import useMediaQuery from "@/hooks/useMediaQuery";
import { ArticleCarouselProps, VideoModalDialogProps } from "@/types";

const VideoModalDialog = dynamic(() => import("@/components/AutogeneratedCarousel/VideoModalDialog"), { ssr: false });

export default function ArticleCarousel({ entry, fallbackImage }: ArticleCarouselProps) {
  const isTablet = useMediaQuery("(min-width: 768px)");
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [videoItemModal, setVideoModal] = useState<VideoModalDialogProps | null>(null);
  const isMobile = useMediaQuery("(max-width: 600px)");
  const locale = useLocale();
  const t = useTranslations();

  return (
    <section className="flex flex-col md:items-end md:justify-end">
      {entry.internalName !== "" ? (
        <div className="w-full">
          <div
            className={`flex items-center ${entry.centeredTitle ? "justify-center" : "ms-m md:ms-[calc(100%_*_2_/_24)]"} md:justify-normal gap-l ${entry.centeredTitle ? "m:w-full" : "md:w-4/5"} mt-[40px]`}
          >
            <h2
              className={`font-primary uppercase text-l mt-3xl ${entry.centeredTitle ? "md:mx-auto md:text-xl text-center" : "w-3/4 md:text-l"} md:mb-l md:mt-xxl`}
            >
              {entry.internalName}
            </h2>
            {!entry.centeredTitle && entry.cta && (
              <Link href={getLink(entry.cta, locale)} className="ml-auto text-s font-medium underline underline-offset-4 mt-3xl md:mb-l md:mt-xxl">
                {entry.cta.label}
              </Link>
            )}
          </div>
        </div>
      ) : null}
      <div className="w-full md:mx-auto md:mr-0 md:pr-0">
        <div className="mt-l md:mt-m mb-xl ">
          <div className="mb-[44px]">
            <VideoModalDialog title={videoItemModal?.title} video={videoItemModal?.video} closeModal={() => setVideoModal(null)} />
            <Swiper
              className={`generated-carousel generated-carousel-article`}
              slidesPerView="auto"
              centeredSlidesBounds={isMobile ? true : false}
              spaceBetween={isTablet ? 22 : 16}
              navigation={{
                nextEl: `.swiper-button-next-${entry.id}-article-tile`,
                prevEl: `.swiper-button-prev-${entry.id}-article-tile`,
              }}
              pagination={{ type: "progressbar", el: `.swiper-pagination-carousel-${entry.id}-article-tile` }}
              onSlideChange={(swiper) => setCurrentSlide(swiper.activeIndex)}
              onSwiper={(swiper) => setCurrentSlide(swiper.activeIndex)}
              centeredSlides={!isTablet ? true : false}
              autoHeight={false}
              watchSlidesProgress={true}
              slidesOffsetAfter={isMobile ? 16 : 0}
            >
              {entry.cards?.map &&
                entry.cards?.map((item, index) => (
                  <SwiperSlide className="!w-fit" key={`autogenerated-${index}`}>
                    <article className="relative rounded-lg overflow-hidden shadow-strong mb-m w-[256px] h-[312px] ">
                      {item.video ? (
                        <>
                          {(item.image || fallbackImage) && (
                            <div className="relative">
                              <Image
                                alt={item.image?.title || fallbackImage?.title || ""}
                                className="aspect-3-4 shadow-strong"
                                src={item.image?.url || fallbackImage?.url}
                                width={256}
                                height={311}
                              />
                              <div className="absolute w-full h-full top-0 left-0 inset-0 img-overlay-5"></div>
                            </div>
                          )}
                          <div className="absolute bottom-0 left-0 p-m">
                            <h3 className="font-medium text-white text-m mb-m">
                              {/* <a href={`/${locale}/` + "d-hub/" + entry.articleType + "/" + item.slug}>{item.internalName}</a> */}
                              <a
                                href={`/${locale}/${articleTypeWithoutDhub.includes(entry.articleType) ? "offers-and-deals/" : "d-hub/"}${entry.articleType}/${item.slug}`}
                              >
                                {item.internalName}
                              </a>
                            </h3>
                            <Button
                              size={ButtonSize.SMALL}
                              variant={ButtonVariant.PRIMARY}
                              theme={ButtonTheme.WHITE}
                              onClick={() => {
                                setVideoModal({
                                  title: item.internalName,
                                  video: item.video,
                                });
                                ctaClickTracking(item?.cta?.label || "", "ArticalCarousel", "ArticalCarousel");
                              }}
                              customClass="flex items-center gap-xxs border-none pr-[20px] font-medium "
                            >
                              <>
                                <Icon name="play" size="16" title={item.cta?.label} className="pointer-events-none" />
                                {item.cta?.label}
                              </>
                            </Button>
                          </div>
                        </>
                      ) : (
                        <div key="0">
                          {(item.image || fallbackImage) && (
                            <div className="relative">
                              <Image
                                key={`article-image-${item.internalName}`}
                                alt={item.image?.title || fallbackImage?.title || ""}
                                src={item.image?.url || fallbackImage?.url}
                                width={256}
                                height={192}
                                className="aspect-4-3"
                              />
                              <div className="absolute w-full h-full top-0 left-0 inset-0 img-overlay-5"></div>
                            </div>
                          )}

                          <div className="flex flex-col bg-white px-s pt-xs pb-s gap-xs font-secondary-light text-s">
                            <p>
                              {item.postDate &&
                                new Date(item.postDate).toLocaleString("default", {
                                  month: "long",
                                  day: "numeric",
                                  year: "numeric",
                                })}
                              {item.articleType && (
                                <span className="font-secondary-light capitalize before:content-['\2022'] before:ml-xxs before:mr-xs">
                                  {t(`label.common.${item.articleType}`).replace("-", " ")}
                                </span>
                              )}
                            </p>
                            <h3 className="font-medium text-m">
                              {/* <a href={`/${locale}/` + "d-hub/" + item.articleType + "/" + item.slug}>{item.internalName}</a> */}
                              <a
                                href={`/${locale}/${!articleTypeWithoutDhub.includes(entry.articleType) ? "offers-and-deals" : "d-hub/"}${!articleTypeWithoutDhub.includes(item.articleType) ? item.articleType : ""}/${item.slug}`}
                              >
                                {item.internalName}
                              </a>
                            </h3>
                            {item.tags && item.tags.length > 0 && (
                              <p className="flex items-center">
                                <span key="0" className="font-secondary-light">
                                  {t("article.tag." + item.tags[0])}
                                </span>
                                {item.tags.length > 1 && (
                                  <span key="1" className="font-secondary-light">
                                    <span className="ml-xs mr-xxs">&#8226; </span>
                                    {t("article.tag." + item.tags[1])}
                                  </span>
                                )}
                              </p>
                            )}
                          </div>
                          {item.cta && (
                            <Link href={item.cta.link.url} className="absolute top-0 left-0 z-10 w-full h-full">
                              <span className="sr-only">{item.cta.label}</span>
                            </Link>
                          )}
                        </div>
                      )}
                    </article>
                  </SwiperSlide>
                ))}
            </Swiper>
            {entry.cards.length >= 4 && (
              <CardCarouselBottomNavigation
                carouselId={`${entry.id}-article-tile`}
                customClass="mt-m md:mt-[40px]"
                cta={entry.cta}
                variant={!entry.centeredTitle}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
